import { useMutation, useQuery } from "@apollo/client";
import * as Popover from "@radix-ui/react-popover";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import Icon from "src/components/Icon";
import { SearchInput } from "src/components/Input";
import {
  ATTACH_EVENT_WALLET_TO_SUBACCOUNT,
  UPDATE_BANK_DETAILS,
} from "src/graphql/mutations";
import {
  GetBanksWithRave,
  GetEvent,
  ResolveAccountWithRave,
} from "src/graphql/queries";
import useAppStore from "src/store/utils";
import {
  getBanksWithRave,
  getBanksWithRaveVariables,
  resolveAccountWithRave,
} from "src/types/api";
import {
  AttachEventWalletToSubaccount,
  AttachEventWalletToSubaccountVariables,
  UpdateUserBankDetails,
  UpdateUserBankDetailsVariables,
} from "src/utils/gql_types";
import { v4 as uuidv4 } from "uuid";
import styles from "./styles.module.scss";

interface BankData {
  __typename: string | null;
  id: number | null;
  name: string | null;
  code: string | null;
}

interface Props {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bvn: string;
  setPaymentSetup: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddBeneficiary = ({ bvn, setOpen, setPaymentSetup }: Props) => {
  const [searchValue, setSearchValue] = useState("");

  const navigate = useNavigate();

  const [openBankList, setOpenBankList] = useState<boolean>(false);

  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState<string | null | undefined>("");
  const [selectedOption, setSelectedOption] = useState<BankData>(
    {} as BankData
  );

  const { data: banks, loading: bankLoading } = useQuery<
    getBanksWithRave,
    getBanksWithRaveVariables
  >(GetBanksWithRave, {
    variables: {
      country: "NG",
    },
    fetchPolicy: "cache-and-network",
    onCompleted() {},
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const [updateBankDetails, { loading: updateBankDetailsLoading }] =
    useMutation<UpdateUserBankDetails, UpdateUserBankDetailsVariables>(
      UPDATE_BANK_DETAILS
    );

  const results = !!banks
    ? banks.getBanksWithRave?.filter((bank) =>
        bank?.name
          ?.toLocaleLowerCase()
          .includes(searchValue.toLocaleLowerCase())
      )
    : [];

  const onOptionClicked = (bank: BankData | null) => {
    if (bank) {
      setSelectedOption(bank);
      setOpenBankList(false);
    }
  };

  const openChange = (open: boolean) => {
    setOpenBankList(open);
  };

  const {
    data: resolvedAccount,
    loading: resolvedAccountLoading,
    error: resolvedAccountError,
  } = useQuery<resolveAccountWithRave>(ResolveAccountWithRave, {
    variables: {
      accountNumber: accountNumber,
      accountBank: selectedOption.code,
    },
    skip: accountNumber.length !== 10,
    fetchPolicy: "cache-and-network",
    onCompleted(res) {
      setAccountName(res.resolveAccountWithRave?.accountName);
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const [attachEventWalletToSubaccount] = useMutation<
    AttachEventWalletToSubaccount,
    AttachEventWalletToSubaccountVariables
  >(ATTACH_EVENT_WALLET_TO_SUBACCOUNT);

  // const setIsPaymentSetup = useContext(AppContext)?.setIsPaymentSetup;

  const { event } = useAppStore((state) => state);
  // const [isBankDetailsUpdated, setIsBankDetailsUpdated] = useState(false);

  // const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // const Disclaimer = () => {
  //   return (
  //     <div className="fixed inset-0 z-50 flex items-center justify-center bg-[black] bg-opacity-70 p-4">
  //       <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl sm:p-8">
  //         <div className="mb-4 flex items-center gap-2">
  //           <Icon iconName="disclaimer" />
  //           <h2 className="text-xl font-semibold text-gray-800">Disclaimer</h2>
  //         </div>

  //         <p className="mb-6 text-sm leading-relaxed text-gray-600 sm:text-base">
  //           The selected account cannot be modified. All cash gifts received
  //           will be automatically transferred to this account.
  //         </p>
  //         <div className="flex justify-between gap-3">
  //           <Button
  //             type="button"
  //             label="Go back"
  //             design="secondary"
  //             onClick={() => setDisclaimerOpen(false)}
  //             className="text-sm sm:text-base"
  //           >
  //             Go back
  //           </Button>
  //           <Button
  //             type="button"
  //             label="I understand"
  //             design="primary"
  //             loader={updateBankDetailsLoading}
  //             onClick={() => {
  //               setDisclaimerOpen(false);
  //               handleSubmit();
  //             }}
  //             className="text-sm sm:text-base"
  //           >
  //             I understand
  //           </Button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const ConfirmAccountModal = () => {
    return (
      <div
        className="relative w-full max-w-lg rounded-2xl border bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-2 text-center text-lg font-bold">
          Confirm Account Details
        </h2>
        <p className="mb-4 text-center text-sm text-gray-600">
          Please confirm your account details before proceeding.
        </p>

        <div className="space-y-3 text-sm">
          <div className="flex items-start justify-between">
            <span className="text-gray-500">Name:</span>
            <span className="max-w-[60%] break-words text-right font-medium">
              {accountName}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Bank:</span>
            <span className="max-w-[60%] break-words text-right font-medium">
              {selectedOption?.name}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Account No:</span>
            <span className="max-w-[60%] break-words text-right font-medium">
              {accountNumber}
            </span>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          <Button
            label="Go back"
            design="secondary"
            onClick={() => setConfirmOpen(false)}
          >
            Go back
          </Button>
          <Button
            loader={updateBankDetailsLoading}
            disabled={updateBankDetailsLoading}
            label="Confirm"
            onClick={() => {
              handleSubmit();
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    );
  };

  const handleSubmit = () => {
    updateBankDetails({
      variables: {
        event_id: event.uuid,
        input: {
          account_number: accountNumber,
          bank_code: selectedOption.code ?? "",
          currency: "ngn",
          bvn,
        },
      },
      onCompleted() {
        // setIsBankDetailsUpdated(true);
        // setOpen(false);
        toast.success("Bank details successfully added");
        // if (isBankDetailsUpdated) {
        attachEventWalletToSubaccount({
          variables: {
            event_id: event.uuid,
            currency: "ngn",
          },
          onCompleted() {
            setPaymentSetup(true);
            setOpen(false);
          },
          onError(error) {
            if (error?.message !== "unauthenticated") {
              toast.error(
                <p className="toast">
                  {error?.message ??
                    "An error occured uploading your bank details. Please try again"}
                </p>
              );
            } else {
              navigate("/clear");
            }
          },
          refetchQueries: [
            { query: GetEvent, variables: { eventId: event?.id } },
          ],
        });
        // }
      },
      onError(error) {
        if (error?.message === "unauthenticated") {
          navigate("/clear");
        } else {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        }
      },
    });
  };

  return (
    <>
      {/* {disclaimerOpen && <Disclaimer />} */}

      {/* <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Portal className="z-[21474836460]">
          <Dialog.Overlay className="fixed left-0 top-0 z-[21474836460] flex h-full w-full items-center justify-center gap-3 overflow-y-auto bg-[#00000080] px-4">
            <Dialog.Content>
              <ConfirmAccountModal />
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root> */}

      {confirmOpen ? (
        <ConfirmAccountModal />
      ) : (
        <div className="max-w-lg rounded-md bg-white p-8">
          <div className="">
            <h1 className="mb-4 text-xl font-semibold">
              Add Naira Beneficiary Account
            </h1>
            <p className="mb-4 text-sm text-gray-500">
              This is where you money would be sent to automatically by our
              payment partners
            </p>

            <div className="">
              <form
                className="flex flex-col gap-4"
                // onSubmit={(e) => {
                //   e.preventDefault();
                //   setDisclaimerOpen(true);
                // }}
              >
                <div
                  className={
                    styles["transferModal--content-form_container__select"]
                  }
                >
                  <label htmlFor="bankCode">Bank Name</label>

                  <Popover.Root
                    open={openBankList}
                    onOpenChange={(open: boolean) => openChange(open)}
                  >
                    <Popover.Trigger>
                      <div
                        className={
                          styles[
                            "transferModal--content-form_container__select-css"
                          ]
                        }
                      >
                        {selectedOption.name || "Select Bank"}

                        {selectedOption.name ? (
                          ""
                        ) : (
                          <Icon iconName="caretDouble" />
                        )}
                      </div>
                    </Popover.Trigger>
                    <ScrollArea.Root>
                      <ScrollArea.Viewport>
                        <Popover.Content
                          className={
                            styles[
                              "transferModal--content-form_container__select-css__content"
                            ]
                          }
                        >
                          <>
                            <div
                              className={
                                styles[
                                  "transferModal--content-form_container__select-css__content__item"
                                ]
                              }
                            >
                              <div
                                className={
                                  styles[
                                    "transferModal--content-form_container__searchbar"
                                  ]
                                }
                              >
                                <SearchInput
                                  icon={<Icon iconName="search" />}
                                  name="Search Banks"
                                  placeholder="Search Banks"
                                  value={searchValue}
                                  onChange={(e: {
                                    target: {
                                      value: React.SetStateAction<string>;
                                    };
                                  }) => setSearchValue(e.target.value)}
                                />
                              </div>
                              {bankLoading ? (
                                <p
                                  className={
                                    styles[
                                      "transferModal--content-form_container__tiny__2"
                                    ]
                                  }
                                >
                                  Loading Banks...
                                </p>
                              ) : !bankLoading ? (
                                results.map((bank) => (
                                  <div
                                    className={
                                      styles[
                                        "transferModal--content-form_container__banks"
                                      ]
                                    }
                                    key={uuidv4()}
                                    onClick={() => {
                                      onOptionClicked(bank);
                                    }}
                                  >
                                    {bank?.name}
                                  </div>
                                ))
                              ) : (
                                <p
                                  className={
                                    styles[
                                      "transferModal--content-form_container__tiny__2"
                                    ]
                                  }
                                >
                                  No Banks!
                                </p>
                              )}
                            </div>
                          </>
                        </Popover.Content>
                      </ScrollArea.Viewport>
                      <ScrollArea.Scrollbar orientation="vertical">
                        <ScrollArea.Thumb />
                      </ScrollArea.Scrollbar>
                      <ScrollArea.Corner />
                    </ScrollArea.Root>
                  </Popover.Root>
                </div>

                <div
                  className={
                    styles["transferModal--content-form_container__group"]
                  }
                >
                  <p>Account Number</p>
                  <input
                    className={
                      styles[
                        "transferModal--content-form_container__select-css__content__input"
                      ]
                    }
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="accountNumber"
                    value={accountNumber}
                    placeholder="Enter Receiver's Account Number"
                    onChange={(e: any) => {
                      // fetchDetails(e.target.value);
                      setAccountNumber(e.target.value);
                    }}
                  />
                  <p className="text-right text-gray-500">
                    {resolvedAccountLoading
                      ? "Getting account..."
                      : resolvedAccountError?.message
                        ? "Couldn't fetch account details"
                        : resolvedAccount?.resolveAccountWithRave
                              ?.accountName === ""
                          ? "Couldn't fetch account details"
                          : resolvedAccount?.resolveAccountWithRave
                              ?.accountName}
                  </p>
                </div>

                <div className="grid w-full rounded-md border border-[#B95900] px-3 py-5">
                  <div className="flex items-center gap-2">
                    <span className="text-red-500">
                      <Icon iconName="note" />
                    </span>
                    <span className="text-sm uppercase text-gray-500">
                      PLS NOTE THAT YOU CANNOT CHANGE THIS ACCOUNT ONCE ADDED
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-500">
                      <Icon iconName="note" />
                    </span>
                    <span className="text-sm uppercase text-gray-500">
                      ALL CASH GIFTS RECEIVED WOULD BE REMITTED HERE
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex gap-4">
                  <Button
                    type="reset"
                    label="Cancel"
                    design="secondary"
                    onClick={() => setOpen(false)}
                    className="text-sm sm:text-base"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    label="Next"
                    design="primary"
                    // onClick={() => setDisclaimerOpen(true)}
                    onClick={() => {
                      setConfirmOpen(true);
                    }}
                    disabled={
                      resolvedAccountLoading || resolvedAccountError?.message
                        ? true
                        : false || accountNumber.length !== 10
                    }
                    className="text-sm sm:text-base"
                    // onClick={() => setOpen(false)}
                  >
                    Next
                  </Button>
                  {/* <Button
                  // type="submit"
                  type="button"
                  label="Add Details"
                  design="primary"
                  loader={updateBankDetailsLoading}
                  onClick={() => setDisclaimerOpen(true)}
                  disabled={
                    resolvedAccountLoading || resolvedAccountError?.message
                      ? true
                      : false || accountNumber.length !== 10
                  }
                  className="text-sm sm:text-base"
                  // onClick={() => setOpen(false)}
                >
                  Add Details
                </Button> */}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddBeneficiary;
